import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	TextField,
	InputAdornment,
	IconButton,
	Alert,
	CircularProgress,
	Autocomplete,
	Card,
	CardContent,
	Avatar,
	Divider,
	Button,
	Collapse,
	Switch,
	Tooltip
} from "@mui/material";
import {
	Clear as ClearIcon,
	Add as AddIcon,
	Check as CheckIcon,
	KeyboardArrowDown as KeyboardArrowDownIcon,
	KeyboardArrowUp as KeyboardArrowUpIcon,
	LocationOn as LocationOnIcon,
	Euro as EuroIcon,
	RecordVoiceOverOutlined as RecordVoiceOverOutlinedIcon,
	OpenInNewRounded as ViewIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { SelectedTalentsCart } from "./SelectedTalentsCart";
import { useFreelancersProfiles, useFreelancerCountries } from "../../../hooks/useFreelancerProfile";
import { FreelancerProfile } from "../../../models/FreelancerProfile";
import { useFreelancersByProject } from "../../../hooks/useProjectFreelancerProfiles";
import {
	useExpertise,
	useExperience,
	useSectors,
	useBusinessDomains,
	useSkills,
	useTools,
	useLanguages,
	useJobLocations,
	useMissionsDurations
} from "../../../hooks/useReferential";
import { FreelancerProfileSearchRequest } from "../../../models/requests/FreelancerProfileSearchRequest";
import { stringAvatar } from "../../../utils/avatarUtils";
import { zoullooColors } from "../../../theme";
import i18n from "../../../i18n/i18n";
import FreelancerDetails from "../../common/FreelancerDetails";
import { useFreelancerProfileEvaluations } from "../../../hooks/useProjectEvaluations";
import { EvaluatorType } from "../../../models/ProjectEvaluation";
import StarRating from "../evaluation/StarRating";
import { useEnvironmentNavigation } from "../../../hooks/useEnvironmentNavigation";
import { TablePagination, SortOption, SortConfig } from "../../common/pagination";
import { FreelancersProfilesSortField } from "../../../models/enums/FreelancersProfilesSortField";
import { SortDirection } from "../../../models/enums/SortDirection";
import { FreelancerOnboardingStatus } from "../../../models/enums/FreelancerOnboardingStatus";
import { useAuthProvider } from "../../../auth/hooks/auth-provider";
import { usePersistentTalentSelection } from "../../../hooks/usePersistentTalentSelection";

// Component to display freelancer evaluation rating inline
const FreelancerRatingBadge: React.FC<{ freelancerProfileId: number }> = ({ freelancerProfileId }) => {
	const { data: evaluationSummary } = useFreelancerProfileEvaluations(freelancerProfileId, freelancerProfileId > 0, EvaluatorType.Client);

	if (!evaluationSummary?.averageRating || evaluationSummary.totalCount === 0) {
		return null;
	}

	return (
		<Box display="flex" alignItems="center" gap={0.5}>
			<StarRating value={evaluationSummary.averageRating} readOnly size="small" />
			<Typography variant="caption" color="text.secondary">
				({evaluationSummary.totalCount})
			</Typography>
		</Box>
	);
};

interface TalentSelectionProps {
	projectId?: number;
	projectTitle?: string;
	projectNumber?: string;
	onTalentsSubmitted?: () => void;
}

export const TalentSelection: React.FC<TalentSelectionProps> = ({
	projectId,
	projectTitle,
	projectNumber,
	onTalentsSubmitted
}) => {
	const { t } = useTranslation();
	const { navigateByEnvironment } = useEnvironmentNavigation();
	const { user } = useAuthProvider();

	const [nameInputValue, setNameInputValue] = useState<string | null>(null);
	const [nameInputError, setNameInputError] = useState<string>("");
	const [dailyRateMinInput, setDailyRateMinInput] = useState<string>("");
	const [dailyRateMaxInput, setDailyRateMaxInput] = useState<string>("");
	const [selectedTalents, setSelectedTalents] = useState<FreelancerProfile[]>([]);
	const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set());
	const [showAllStatuses, setShowAllStatuses] = useState<boolean>(false);
	const [freelancerProfileSearchRequest, setFreelancerProfileSearchRequest] =
		useState<FreelancerProfileSearchRequest>({
			name: null,
			onboardingStatus: FreelancerOnboardingStatus.Accepted,
			expertiseId: null,
			experienceId: null,
			sectorIds: null,
			businessDomainIds: null,
			expertSkillIds: null,
			confirmedSkillIds: null,
			masteredToolIds: null,
			usedToolIds: null,
			nativeLanguageIds: null,
			fluentLanguageIds: null,
			operationalLanguageIds: null,
			country: null,
			jobPreferredLocationId: null,
			missionsPreferredDurationId: null,
			dailyRateMin: null,
			dailyRateMax: null,
			isTrainer: null,
			languageCode: i18n.language,
			sort: null,
			page: 1,
			pageSize: 10
		});

	const { data: expertiseData } = useExpertise();
	const { data: experienceData } = useExperience();
	const { data: sectorsData } = useSectors();
	const { data: businessDomainsData } = useBusinessDomains();
	const { data: skillsData } = useSkills();
	const { data: toolsData } = useTools();
	const { data: languagesData } = useLanguages();
	const { data: jobLocationsData } = useJobLocations();
	const { data: missionsDurationsData } = useMissionsDurations();
	const { data: countriesData } = useFreelancerCountries(
		freelancerProfileSearchRequest.onboardingStatus != null
			? FreelancerOnboardingStatus[freelancerProfileSearchRequest.onboardingStatus]
			: undefined
	);
	const { data, isLoading, error } = useFreelancersProfiles(
		freelancerProfileSearchRequest
	);
	const { data: associatedFreelancers } = useFreelancersByProject(projectId ?? 0);

	const freelancersProfiles = data?.items || [];
	const totalPages = data?.totalPages || 0;
	const totalCount = data?.totalCount || 0;

	// Persist talent selection in localStorage per user and project
	usePersistentTalentSelection(
		user?.id,
		projectId,
		selectedTalents,
		setSelectedTalents,
		freelancersProfiles
	);

	// Create sort options for talent selection
	const sortOptions: SortOption[] = [
		{
			label: t("enums.freelancersSortField.FirstName"),
			value: FreelancersProfilesSortField.FirstName
		},
		{
			label: t("enums.freelancersSortField.LastName"),
			value: FreelancersProfilesSortField.LastName
		},
		{
			label: t("enums.freelancersSortField.Country"),
			value: FreelancersProfilesSortField.Country
		},
		{
			label: t("enums.freelancersSortField.City"),
			value: FreelancersProfilesSortField.City
		},
		{
			label: t("enums.freelancersSortField.DailyRateMin"),
			value: FreelancersProfilesSortField.DailyRateMin
		},
		{
			label: t("enums.freelancersSortField.DailyRateMax"),
			value: FreelancersProfilesSortField.DailyRateMax
		},
		{
			label: t("enums.freelancersSortField.CreatedAt"),
			value: FreelancersProfilesSortField.CreatedAt
		}
	];

	// Create current sort configuration
	const currentSort: SortConfig = {
		sortField: freelancerProfileSearchRequest.sort?.sortField || undefined,
		sortDirection:
			freelancerProfileSearchRequest.sort?.sortDirection === SortDirection.Desc
				? "Desc"
				: "Asc"
	};

	// Update search with debounce
	useEffect(() => {
		if (nameInputValue === null) return;

		const timeoutId = setTimeout(() => {
			const searchName = nameInputValue.trim();
			setFreelancerProfileSearchRequest(prev => ({
				...prev,
				name: searchName || null,
				page: 1
			}));
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [nameInputValue]);

	// Update daily rate min with debounce
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			const value = dailyRateMinInput ? parseFloat(dailyRateMinInput) : null;
			setFreelancerProfileSearchRequest(prev => ({
				...prev,
				dailyRateMin: value,
				page: 1
			}));
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [dailyRateMinInput]);

	// Update daily rate max with debounce
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			const value = dailyRateMaxInput ? parseFloat(dailyRateMaxInput) : null;
			setFreelancerProfileSearchRequest(prev => ({
				...prev,
				dailyRateMax: value,
				page: 1
			}));
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [dailyRateMaxInput]);

	const handleNameInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		const maxLength = 250;

		if (newValue.length > maxLength) {
			setNameInputError(
				t("pages.applications.filters.nameMaxLengthError", { maxLength })
			);
			return;
		}

		setNameInputError("");
		setNameInputValue(newValue);
	};

	const handlePageChange = (page: number) => {
		setFreelancerProfileSearchRequest(prev => ({
			...prev,
			page
		}));
	};

	const handlePageSizeChange = (pageSize: number) => {
		setFreelancerProfileSearchRequest(prev => ({
			...prev,
			pageSize,
			page: 1
		}));
	};

	// Handle sort change from pagination component
	const handleSortChange = (sort: SortConfig) => {
		setFreelancerProfileSearchRequest(prev => ({
			...prev,
			sort: sort.sortField
				? {
						sortField: sort.sortField as FreelancersProfilesSortField,
						sortDirection:
							sort.sortDirection === "Desc" ? SortDirection.Desc : SortDirection.Asc
				  }
				: null,
			page: 1
		}));
	};

	const handleAddTalent = (talent: FreelancerProfile) => {
		if (!selectedTalents.find(t => t.id === talent.id)) {
			setSelectedTalents([...selectedTalents, talent]);
		}
	};

	const handleRemoveTalent = (talentId: number) => {
		setSelectedTalents(selectedTalents.filter(t => t.id !== talentId));
	};

	const handleClearSelection = () => {
		setSelectedTalents([]);
	};

	const isSelectedTalent = (talentId: number) => {
		return selectedTalents.some(t => t.id === talentId);
	};

	const isAlreadyAssociated = (talentId: number) => {
		return (
			associatedFreelancers?.some(
				(assoc: any) => assoc.freelancerProfileId === talentId
			) || false
		);
	};

	const handleToggleExpansion = (freelancerProfileId: number, index: number) => {
		const panelId = freelancerProfileId?.toString() || `freelancer-profiles-${index}`;
		setExpandedPanels(prev => {
			const newSet = new Set(prev);
			if (newSet.has(panelId)) {
				newSet.delete(panelId);
			} else {
				newSet.add(panelId);
			}
			return newSet;
		});
	};

	const handleResetFilters = () => {
		setNameInputValue(null);
		setNameInputError("");
		setDailyRateMinInput("");
		setDailyRateMaxInput("");
		setShowAllStatuses(false);
		setFreelancerProfileSearchRequest({
			name: null,
			onboardingStatus: FreelancerOnboardingStatus.Accepted,
			expertiseId: null,
			experienceId: null,
			sectorIds: null,
			businessDomainIds: null,
			expertSkillIds: null,
			confirmedSkillIds: null,
			masteredToolIds: null,
			usedToolIds: null,
			nativeLanguageIds: null,
			fluentLanguageIds: null,
			operationalLanguageIds: null,
			country: null,
			jobPreferredLocationId: null,
			missionsPreferredDurationId: null,
			dailyRateMin: null,
			dailyRateMax: null,
			isTrainer: null,
			languageCode: i18n.language,
			sort: null,
			page: 1,
			pageSize: 10
		});
	};

	const getButtonBackgroundColor = (freelancerProfileId: number) => {
		if (isAlreadyAssociated(freelancerProfileId)) {
			return zoullooColors.grey[400];
		}
		if (isSelectedTalent(freelancerProfileId)) {
			return zoullooColors.success.main;
		}
		return zoullooColors.secondary.main;
	};

	const getButtonHoverBackgroundColor = (freelancerProfileId: number) => {
		if (isSelectedTalent(freelancerProfileId)) {
			return zoullooColors.success.dark;
		}
		return zoullooColors.secondary.dark;
	};

	const getButtonIcon = (freelancerProfileId: number) => {
		if (
			isAlreadyAssociated(freelancerProfileId) ||
			isSelectedTalent(freelancerProfileId)
		) {
			return <CheckIcon />;
		}
		return <AddIcon />;
	};

	return (
		<>
			{/* Two-column layout like PDF */}
			<Box
				display="flex"
				gap={3}
				px={{ xs: 2, sm: 4, md: 6 }}
				pt={3}
				minHeight="calc(100% - 70px)"
			>
				{/* Left Column - Main content */}
				<Box flex={1}>
					{/* Project info above cart (only in project mode) */}
					{projectId && (
						<Box mb={2}>
							<Typography variant="h5" sx={{ fontWeight: 500 }}>
								{projectTitle}
							</Typography>
							<Typography variant="body1" color="text.secondary">
								{projectNumber}
							</Typography>
						</Box>
					)}

					{/* Selection Cart */}
					{selectedTalents.length > 0 && (
						<Box
							sx={{
								mb: 3
							}}
						>
							<SelectedTalentsCart
								selectedTalents={selectedTalents}
								projectId={projectId}
								onRemoveTalent={handleRemoveTalent}
								onClearSelection={handleClearSelection}
								onTalentsSubmitted={onTalentsSubmitted}
							/>
						</Box>
					)}

					{/* Loading */}
					{isLoading && (
						<Box display="flex" justifyContent="center" py={4}>
							<CircularProgress />
						</Box>
					)}

					{/* Error */}
					{error && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{t("project.talentSelection.errorLoading")}
						</Alert>
					)}

					{/* Results */}
					{!isLoading && !error && (
						<>
							{/* Results counter */}
							{freelancersProfiles.length > 0 && (
								<Box mb={2}>
									<Typography variant="body1" color="text.secondary">
										{t("project.talentSelection.talentsFound", {
											current: freelancersProfiles.length,
											total: totalCount
										})}
									</Typography>
								</Box>
							)}

							{freelancersProfiles.length === 0 ? (
								<Box display="flex" justifyContent="center" py={4}>
									<Typography variant="body1" color="text.secondary">
										{t("project.talentSelection.noResults")}
									</Typography>
								</Box>
							) : (
								<>
									{/* Freelancers List - Same structure as TAM */}
									<Card>
										{freelancersProfiles.map((freelancerProfile, index) => {
											return (
												<CardContent
													key={freelancerProfile.id}
													sx={{
														pt: index === 0 ? "32px" : "auto"
													}}
												>
													{/* Top row with avatar and info */}
													<Box
														display="flex"
														alignItems="flex-start"
														gap={3}
														sx={{
															opacity: isAlreadyAssociated(freelancerProfile.id) ? 0.7 : 1
														}}
													>
														{/* Avatar */}
														<Avatar
															{...stringAvatar(
																freelancerProfile.freelancer?.user?.firstName ?? "",
																freelancerProfile.freelancer?.user?.lastName ?? ""
															)}
															src={
																freelancerProfile.freelancer?.picture?.urlPath
																	? `${process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL}/${process.env.REACT_APP_FREELANCER_PICTURES_CONTAINER_NAME}/${freelancerProfile.freelancer?.picture?.urlPath}`
																	: undefined
															}
														/>

														{/* Main content */}
														<Box flex={1} display="flex" flexDirection="column" gap={0}>
															{/* Ligne 1: Prénom Nom | Expertise - Niveau d'expérience */}
															<Box display="flex" alignItems="center" gap={1}>
																<Typography
																	variant="h6"
																	component="span"
																	fontWeight={600}
																>
																	{freelancerProfile.freelancer?.user?.firstName}{" "}
																	{freelancerProfile.freelancer?.user?.lastName}
																</Typography>
																<FreelancerRatingBadge freelancerProfileId={freelancerProfile.id} />
																{(freelancerProfile.expertise?.label ||
																	freelancerProfile.experience?.label) && (
																	<>
																		<Typography
																			variant="body1"
																			component="span"
																			color="text.secondary"
																		>
																			|
																		</Typography>
																		<Typography variant="body1" component="span">
																			{freelancerProfile?.expertise?.label &&
																				t(
																					`Expertises.${freelancerProfile.expertise?.label}`
																				)}{" "}
																			{freelancerProfile.expertise?.label &&
																				freelancerProfile.experience?.label &&
																				"- "}
																			{freelancerProfile.experience?.label &&
																				t(
																					`Experiences.${freelancerProfile.experience?.label}`
																				)}
																		</Typography>
																	</>
																)}
															</Box>

															{/* Lignes 2, 3, 4 - Cachées quand les détails sont ouverts */}
															<Collapse
																in={
																	!expandedPanels.has(
																		freelancerProfile.id?.toString() ||
																			`freelancer-profiles-${index}`
																	)
																}
																timeout={300}
																sx={{ mt: 0.5 }}
															>
																{/* Ligne 2: 📍 Ville Code Pays | 💰 TJM min-max | 💬 lang|lang */}
																<Box display="flex" alignItems="center" gap={1} mb={0.5}>
																	{/* Icône et localisation */}
																	<Box display="flex" alignItems="center" gap={0.5}>
																		<LocationOnIcon
																			sx={{ fontSize: 16, color: "text.secondary" }}
																		/>
																		<Typography variant="body2" component="span">
																			{freelancerProfile.freelancer?.address?.city || ""}
																			{freelancerProfile.freelancer?.address?.city &&
																				freelancerProfile.freelancer?.address
																					?.countryCode &&
																				" "}
																			{freelancerProfile.freelancer?.address
																				?.countryCode || ""}
																		</Typography>
																	</Box>

																	{(freelancerProfile.dailyRateMin ||
																		freelancerProfile.dailyRateMax) && (
																		<>
																			<Typography
																				variant="body2"
																				component="span"
																				color="text.secondary"
																			>
																				|
																			</Typography>
																			{/* Icône et tarifs */}
																			<Box display="flex" alignItems="center" gap={0.5}>
																				<EuroIcon
																					sx={{ fontSize: 16, color: "text.secondary" }}
																				/>
																				<Typography variant="body2" component="span">
																					{freelancerProfile.dailyRateMin &&
																						`${freelancerProfile.dailyRateMin}`}
																					{freelancerProfile.dailyRateMin &&
																						freelancerProfile.dailyRateMax &&
																						" - "}
																					{freelancerProfile.dailyRateMax &&
																						`${freelancerProfile.dailyRateMax}`}
																				</Typography>
																			</Box>
																		</>
																	)}

																	{freelancerProfile.freelancer?.languages &&
																		freelancerProfile.freelancer.languages.length > 0 && (
																			<>
																				<Typography
																					variant="body2"
																					component="span"
																					color="text.secondary"
																				>
																					|
																				</Typography>
																				{/* Icône et langues */}
																				<Box display="flex" alignItems="center" gap={0.5}>
																					<RecordVoiceOverOutlinedIcon
																						sx={{ fontSize: 16, color: "text.secondary" }}
																					/>
																					<Typography variant="body2" component="span">
																						{freelancerProfile.freelancer.languages
																							.map((lang: any) =>
																								t(`Languages.${lang.language.label}`)
																							)
																							.join(" | ")}
																					</Typography>
																				</Box>
																			</>
																		)}
																</Box>

																{/* Ligne 3: Secteur : {secteur1} | {secteur2} */}
																{freelancerProfile.sectors?.length > 0 && (
																	<Box
																		display="flex"
																		alignItems="center"
																		gap={1}
																		mb={0.5}
																	>
																		<Typography
																			variant="body2"
																			component="span"
																			fontWeight={500}
																		>
																			{t("project.talentSelection.sector")} :
																		</Typography>
																		<Typography variant="body2" component="span">
																			{freelancerProfile.sectors
																				.map((sector: any) =>
																					t(`Sectors.${sector.sector.label}`)
																				)
																				.join(" | ")}
																		</Typography>
																	</Box>
																)}

																{/* Ligne 4: Domaines : {domaine1} | {domaine2} */}
																{freelancerProfile.businessDomains?.length > 0 && (
																	<Box display="flex" alignItems="center" gap={1}>
																		<Typography
																			variant="body2"
																			component="span"
																			fontWeight={500}
																		>
																			{t("project.talentSelection.domains")} :
																		</Typography>
																		<Typography variant="body2" component="span">
																			{freelancerProfile.businessDomains
																				.map((domain: any) =>
																					t(
																						`BusinessDomains.${domain.businessDomain.label}`
																					)
																				)
																				.join(" | ")}
																		</Typography>
																	</Box>
																)}
															</Collapse>
														</Box>

														{/* Right side with buttons */}
														<Box display="flex" alignItems="center" gap={1}>
															{/* Show "already associated" text inline with buttons */}
															{isAlreadyAssociated(freelancerProfile.id) && (
																<Typography
																	variant="caption"
																	color="text.secondary"
																	sx={{ mr: 1 }}
																>
																	{t("project.talentSelection.alreadyAssociated")}
																</Typography>
															)}

															{/* Buttons row */}
															<Box display="flex" gap={1} alignItems="center">
																{/* Details toggle button */}
																<IconButton
																	onClick={() =>
																		handleToggleExpansion(freelancerProfile.id, index)
																	}
																	sx={{
																		width: 32,
																		height: 32,
																		bgcolor: "transparent",
																		color: zoullooColors.primary.main,
																		border: `1px solid ${zoullooColors.primary.main}`,
																		"&:hover": {
																			bgcolor: zoullooColors.primary.main,
																			color: "white",
																			transform: "scale(1.05)"
																		},
																		transition: "all 0.2s ease"
																	}}
																>
																	{expandedPanels.has(
																		freelancerProfile.id?.toString() ||
																			`freelancer-profiles-${index}`
																	) ? (
																		<KeyboardArrowDownIcon />
																	) : (
																		<KeyboardArrowUpIcon />
																	)}
																</IconButton>

																{/* View details button */}
																<Tooltip title={t("project.viewFreelancerDetails")}>
																	<IconButton
																		onClick={() => {
																			navigateByEnvironment(
																				`/applications/talent/${freelancerProfile.key}`
																			);
																		}}
																		sx={{
																			width: 32,
																			height: 32,
																			bgcolor: "transparent",
																			color: zoullooColors.secondary.main,
																			border: `1px solid ${zoullooColors.secondary.main}`,
																			"&:hover": {
																				bgcolor: zoullooColors.secondary.main,
																				color: "white",
																				transform: "scale(1.05)"
																			},
																			transition: "all 0.2s ease"
																		}}
																	>
																		<ViewIcon />
																	</IconButton>
																</Tooltip>

																{/* Add to cart button */}
																<IconButton
																	onClick={() => handleAddTalent(freelancerProfile)}
																	disabled={
																		isSelectedTalent(freelancerProfile.id) ||
																		isAlreadyAssociated(freelancerProfile.id)
																	}
																	sx={{
																		width: 40,
																		height: 40,
																		bgcolor: getButtonBackgroundColor(
																			freelancerProfile.id
																		),
																		color: "white",
																		border: "2px solid white",
																		boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
																		"&:hover": {
																			bgcolor: getButtonHoverBackgroundColor(
																				freelancerProfile.id
																			),
																			transform: "scale(1.05)"
																		},
																		"&:disabled": {
																			bgcolor: zoullooColors.success.main,
																			color: "white",
																			transform: "none"
																		},
																		transition: "all 0.2s ease"
																	}}
																>
																	{getButtonIcon(freelancerProfile.id)}
																</IconButton>
															</Box>
														</Box>
													</Box>

													{/* Freelancer Details Panel */}
													<Collapse
														in={expandedPanels.has(
															freelancerProfile.id?.toString() ||
																`freelancer-profiles-${index}`
														)}
														timeout={400}
														unmountOnExit
													>
														<FreelancerDetails
															freelancerProfileId={freelancerProfile.id}
															isExpanded={expandedPanels.has(
																freelancerProfile.id?.toString() ||
																	`freelancer-profiles-${index}`
															)}
															hideLastExchange={true}
															key={`freelancer-details-${freelancerProfile.id}`}
														/>
													</Collapse>

													{index < freelancersProfiles.length - 1 && (
														<Divider
															sx={{ height: 16 }}
															orientation="horizontal"
															variant="middle"
														/>
													)}
												</CardContent>
											);
										})}
									</Card>
								</>
							)}
						</>
					)}
				</Box>

				{/* Right Column - Filters Sidebar like PDF */}
				<Box
					sx={{
						width: 300,
						bgcolor: "background.paper",
						border: `1px solid ${zoullooColors.grey[400]}`,
						borderRadius: 4,
						p: 2
					}}
				>
					<Typography variant="h6" sx={{ mb: 2 }}>
						{t("project.talentSelection.filters")}
					</Typography>

					{/* Search Filter */}
					<Box mb={3}>
						<TextField
							fullWidth
							label={t("pages.applications.filters.nameLabel")}
							value={nameInputValue ?? ""}
							onChange={handleNameInputChange}
							size="small"
							error={!!nameInputError}
							helperText={nameInputError}
							variant="outlined"
							placeholder={t("pages.applications.filters.namePlaceholder")}
							slotProps={{
								input: {
									endAdornment: nameInputValue && (
										<InputAdornment position="end">
											<IconButton
												aria-label="clear search"
												onClick={() => {
													setNameInputValue("");
													setNameInputError("");
												}}
												edge="end"
												size="small"
											>
												<ClearIcon />
											</IconButton>
										</InputAdornment>
									)
								}
							}}
						/>
					</Box>

					{/* Expertise Filter */}
					<Box mb={3}>
						<Autocomplete
							fullWidth
							size="small"
							options={expertiseData || []}
							groupBy={option => option.category}
							getOptionLabel={option => t(`Expertises.${option.label}`)}
							value={
								freelancerProfileSearchRequest.expertiseId
									? (expertiseData ?? []).find(
											exp => exp.id === freelancerProfileSearchRequest.expertiseId
									  )
									: null
							}
							onChange={(_, newValue) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									expertiseId: newValue?.id || null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.expertise")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Experience Filter */}
					<Box mb={3}>
						<Autocomplete
							fullWidth
							size="small"
							options={experienceData || []}
							getOptionLabel={option => t(`Experiences.${option.label}`)}
							value={
								freelancerProfileSearchRequest.experienceId
									? (experienceData ?? []).find(
											exp => exp.id === freelancerProfileSearchRequest.experienceId
									  )
									: null
							}
							onChange={(_, newValue) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									experienceId: newValue?.id || null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.experience")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Sectors Filter */}
					<Box mb={3}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							fullWidth
							size="small"
							options={sectorsData || []}
							getOptionLabel={option => t(`Sectors.${option.label}`)}
							value={
								freelancerProfileSearchRequest.sectorIds
									? (sectorsData ?? []).filter(s =>
											freelancerProfileSearchRequest.sectorIds?.includes(s.id)
									  )
									: []
							}
							onChange={(_, newValues) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									sectorIds: newValues.length > 0 ? newValues.map(v => v.id) : null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.sectors")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Business Domains Filter */}
					<Box mb={3}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							fullWidth
							size="small"
							options={businessDomainsData || []}
							getOptionLabel={option => t(`BusinessDomains.${option.label}`)}
							value={
								freelancerProfileSearchRequest.businessDomainIds
									? (businessDomainsData ?? []).filter(bd =>
											freelancerProfileSearchRequest.businessDomainIds?.includes(bd.id)
									  )
									: []
							}
							onChange={(_, newValues) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									businessDomainIds:
										newValues.length > 0 ? newValues.map(v => v.id) : null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.domains")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Expert Skills Filter */}
					<Box mb={3}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							fullWidth
							size="small"
							options={skillsData || []}
							getOptionLabel={option => t(`Skills.${option.label}`)}
							value={
								freelancerProfileSearchRequest.expertSkillIds
									? (skillsData ?? []).filter(s =>
											freelancerProfileSearchRequest.expertSkillIds?.includes(s.id)
									  )
									: []
							}
							onChange={(_, newValues) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									expertSkillIds: newValues.length > 0 ? newValues.map(v => v.id) : null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.expertSkills")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Confirmed Skills Filter */}
					<Box mb={3}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							fullWidth
							size="small"
							options={skillsData || []}
							getOptionLabel={option => t(`Skills.${option.label}`)}
							value={
								freelancerProfileSearchRequest.confirmedSkillIds
									? (skillsData ?? []).filter(s =>
											freelancerProfileSearchRequest.confirmedSkillIds?.includes(s.id)
									  )
									: []
							}
							onChange={(_, newValues) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									confirmedSkillIds:
										newValues.length > 0 ? newValues.map(v => v.id) : null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.confirmedSkills")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Mastered Tools Filter */}
					<Box mb={3}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							fullWidth
							size="small"
							options={toolsData || []}
							getOptionLabel={(option: any) => option.label || ""}
							value={
								freelancerProfileSearchRequest.masteredToolIds
									? ((toolsData as any[]) ?? []).filter((t: any) =>
											freelancerProfileSearchRequest.masteredToolIds?.includes(t.id)
									  )
									: []
							}
							onChange={(_, newValues) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									masteredToolIds: newValues.length > 0 ? newValues.map(v => v.id) : null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.masteredTools")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Used Tools Filter */}
					<Box mb={3}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							fullWidth
							size="small"
							options={toolsData || []}
							getOptionLabel={(option: any) => option.label || ""}
							value={
								freelancerProfileSearchRequest.usedToolIds
									? ((toolsData as any[]) ?? []).filter((t: any) =>
											freelancerProfileSearchRequest.usedToolIds?.includes(t.id)
									  )
									: []
							}
							onChange={(_, newValues) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									usedToolIds: newValues.length > 0 ? newValues.map(v => v.id) : null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.usedTools")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Native Languages Filter */}
					<Box mb={3}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							fullWidth
							size="small"
							options={languagesData || []}
							getOptionLabel={option => t(`Languages.${option.label}`)}
							value={
								freelancerProfileSearchRequest.nativeLanguageIds
									? (languagesData ?? []).filter(l =>
											freelancerProfileSearchRequest.nativeLanguageIds?.includes(l.id)
									  )
									: []
							}
							onChange={(_, newValues) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									nativeLanguageIds:
										newValues.length > 0 ? newValues.map(v => v.id) : null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.nativeLanguages")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Fluent Languages Filter */}
					<Box mb={3}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							fullWidth
							size="small"
							options={languagesData || []}
							getOptionLabel={option => t(`Languages.${option.label}`)}
							value={
								freelancerProfileSearchRequest.fluentLanguageIds
									? (languagesData ?? []).filter(l =>
											freelancerProfileSearchRequest.fluentLanguageIds?.includes(l.id)
									  )
									: []
							}
							onChange={(_, newValues) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									fluentLanguageIds:
										newValues.length > 0 ? newValues.map(v => v.id) : null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.fluentLanguages")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Operational Languages Filter */}
					<Box mb={3}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							fullWidth
							size="small"
							options={languagesData || []}
							getOptionLabel={option => t(`Languages.${option.label}`)}
							value={
								freelancerProfileSearchRequest.operationalLanguageIds
									? (languagesData ?? []).filter(l =>
											freelancerProfileSearchRequest.operationalLanguageIds?.includes(
												l.id
											)
									  )
									: []
							}
							onChange={(_, newValues) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									operationalLanguageIds:
										newValues.length > 0 ? newValues.map(v => v.id) : null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.operationalLanguages")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Country Filter */}
					<Box mb={3}>
						<Autocomplete
							fullWidth
							size="small"
							options={
								[...(countriesData || [])].sort((a, b) => {
									const displayNames = new Intl.DisplayNames([i18n.language], { type: "region" });
									return (displayNames.of(a) || a).localeCompare(displayNames.of(b) || b, i18n.language);
								})
							}
							getOptionLabel={(option: string) => {
								const displayNames = new Intl.DisplayNames([i18n.language], { type: "region" });
								return displayNames.of(option) || option;
							}}
							value={freelancerProfileSearchRequest.country || null}
							onChange={(_, newValue) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									country: newValue || null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.country")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

				{/* Job Preferred Location Filter */}
					<Box mb={3}>
						<Autocomplete
							fullWidth
							size="small"
							options={jobLocationsData || []}
							getOptionLabel={option => t(`JobLocations.${option.label}`)}
							value={
								freelancerProfileSearchRequest.jobPreferredLocationId
									? (jobLocationsData ?? []).find(
											jl =>
												jl.id === freelancerProfileSearchRequest.jobPreferredLocationId
									  )
									: null
							}
							onChange={(_, newValue) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									jobPreferredLocationId: newValue?.id || null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.jobPreferredLocation")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Missions Preferred Duration Filter */}
					<Box mb={3}>
						<Autocomplete
							fullWidth
							size="small"
							options={missionsDurationsData || []}
							getOptionLabel={option => t(`MissionsDurations.${option.label}`)}
							value={
								freelancerProfileSearchRequest.missionsPreferredDurationId
									? (missionsDurationsData ?? []).find(
											md =>
												md.id ===
												freelancerProfileSearchRequest.missionsPreferredDurationId
									  )
									: null
							}
							onChange={(_, newValue) => {
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									missionsPreferredDurationId: newValue?.id || null,
									page: 1
								}));
							}}
							renderInput={params => (
								<TextField
									{...params}
									label={t("project.talentSelection.missionsPreferredDuration")}
									variant="outlined"
								/>
							)}
						/>
					</Box>

					{/* Daily Rate Filter - Min and Max on same line */}
					<Box mb={3}>
						<Box display="flex" gap={1}>
							<TextField
								fullWidth
								size="small"
								type="number"
								label={t("project.talentSelection.dailyRateMin")}
								variant="outlined"
								value={dailyRateMinInput}
								onChange={e => {
									setDailyRateMinInput(e.target.value);
								}}
								slotProps={{
									htmlInput: { min: 0 }
								}}
							/>
							<TextField
								fullWidth
								size="small"
								type="number"
								label={t("project.talentSelection.dailyRateMax")}
								variant="outlined"
								value={dailyRateMaxInput}
								onChange={e => {
									setDailyRateMaxInput(e.target.value);
								}}
								slotProps={{
									htmlInput: { min: 0 }
								}}
							/>
						</Box>
					</Box>

					{/* Trainer Filter */}
					<Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
						<Typography variant="body2" color="text.secondary">
							{t("project.talentSelection.trainer")}
						</Typography>
						<Box display="flex" alignItems="center">
							<Typography variant="body2" sx={{ mr: 0.5 }}>
								{t("project.talentSelection.no")}
							</Typography>
							<Switch
								checked={freelancerProfileSearchRequest.isTrainer || false}
								onChange={e => {
									setFreelancerProfileSearchRequest(prev => ({
										...prev,
										isTrainer: e.target.checked ? true : null,
										page: 1
									}));
								}}
								size="small"
							/>
							<Typography variant="body2" sx={{ ml: 0.5 }}>
								{t("project.talentSelection.yes")}
							</Typography>
						</Box>
					</Box>

					{/* Show All Talents Filter */}
					<Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
						<Typography variant="body2" color="text.secondary">
							{t("project.talentSelection.allTalents")}
						</Typography>
						<Box display="flex" alignItems="center">
							<Typography variant="body2" sx={{ mr: 0.5 }}>
								{t("project.talentSelection.no")}
							</Typography>
							<Switch
								checked={showAllStatuses}
								onChange={e => {
									const checked = e.target.checked;
									setShowAllStatuses(checked);
									setFreelancerProfileSearchRequest(prev => ({
										...prev,
										onboardingStatus: checked
											? null
											: FreelancerOnboardingStatus.Accepted,
										page: 1
									}));
								}}
								size="small"
							/>
							<Typography variant="body2" sx={{ ml: 0.5 }}>
								{t("project.talentSelection.yes")}
							</Typography>
						</Box>
					</Box>

					{/* Reset Button */}
					<Box>
						<Button
							fullWidth
							variant="contained"
							onClick={handleResetFilters}
							sx={{
								backgroundColor: zoullooColors.secondary.main,
								height: 32,
								"&:hover": {
									backgroundColor: zoullooColors.secondary.dark
								}
							}}
						>
							{t("project.talentSelection.resetFilters")}
						</Button>
					</Box>
				</Box>
			</Box>

			{/* Unified Pagination with Sort */}
			{!isLoading && freelancersProfiles.length > 0 && (
				<TablePagination
					currentPage={freelancerProfileSearchRequest.page}
					pageSize={freelancerProfileSearchRequest.pageSize}
					totalCount={totalCount}
					totalPages={totalPages}
					onPageChange={handlePageChange}
					onPageSizeChange={handlePageSizeChange}
					pageSizeOptions={[10, 25, 50]}
					sortOptions={sortOptions}
					currentSort={currentSort}
					onSortChange={handleSortChange}
					disabled={false}
					sticky={true}
					mx={{ xs: 2, sm: 4, md: 6 }}
				/>
			)}
		</>
	);
};
